import { FC, ReactNode, useCallback, useMemo, useState } from 'react'
import { useTranslate } from 'react-admin'
import { Node, Edge, ReactFlowProvider } from '@xyflow/react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import WorkflowToolbar from './WorkflowToolbar'
import WorkflowCanvas from './WorkflowCanvas'
import WorkflowSidebar from './WorkflowSidebar'
import NodeConfigPanel from './NodeConfigPanel'
import CopilotPanel from './CopilotPanel'
import WorkflowValidationDialog from './components/WorkflowValidationDialog'
import { useWorkflowState } from './hooks/useWorkflowState'
import { useAutoLayout } from './hooks/useAutoLayout'
import { useUnsavedChangesGuard } from './hooks/useUnsavedChangesGuard'
import { ExpressionAutocompleteProvider } from './autocomplete/ExpressionContext'
import { useFunctionsRegistry } from './autocomplete/functionsRegistry'
import { buildParentIndex, loopVariablesAtNode, predecessorsOf } from './autocomplete/graphReachability'
import {
  validateWorkflow,
  WorkflowValidationResult,
} from './autocomplete/validateWorkflow'
import { extractFieldPaths } from './config/parameterUtils'
import { SidebarDragData, WorkflowNodeData } from './types'

const deriveAutocompleteInputs = (
  nodes: Node[],
  edges: Edge[],
  selectedNodeId: string | null,
): {
  availableNodeIds: string[]
  triggerKeys: string[]
  nodeOutputKeys: Record<string, string[]>
  loopVariableNames: string[]
  nodeMeta: Record<string, { label: string; type: string }>
} => {
  // Only reachable predecessors — downstream nodes resolve to "" at runtime.
  const parents = buildParentIndex(edges)
  const reachable = selectedNodeId
    ? predecessorsOf(selectedNodeId, parents)
    : new Set(nodes.map((n) => n.id))
  const availableNodeIds = Array.from(reachable).sort()
  const loopVariableNames = selectedNodeId
    ? loopVariablesAtNode(selectedNodeId, nodes, parents)
    : []

  // Only suggest fields of triggers the node is wired to — fields of an
  // unconnected trigger resolve to "" at runtime.
  const triggerKeys = new Set<string>()
  const nodeOutputKeys: Record<string, string[]> = {}
  const nodeMeta: Record<string, { label: string; type: string }> = {}
  nodes.forEach((n) => {
    const data = n.data as WorkflowNodeData | undefined
    if (data?.type) {
      // eslint-disable-next-line security/detect-object-injection
      nodeMeta[n.id] = { label: data.label || n.id, type: data.type }
    }
    if (data?.type === 'trigger' && data.triggerType === 'mcp') {
      const schema = data.mcpToolInputSchema as
        | { properties?: Record<string, unknown> }
        | undefined
      const keys = Object.keys(schema?.properties ?? {})
      if (reachable.has(n.id)) {
        keys.forEach((k) => triggerKeys.add(k))
      }
      // eslint-disable-next-line security/detect-object-injection
      nodeOutputKeys[n.id] = keys.slice().sort()
    }
    if (data?.type === 'tool_call') {
      const fields = data.lastOutputFields
      if (Array.isArray(fields) && fields.length > 0) {
        // eslint-disable-next-line security/detect-object-injection
        nodeOutputKeys[n.id] = fields.slice().sort()
      }
    }
    // Object examples only: the executor passes parsed JSON objects through verbatim.
    if (data?.type === 'ai_call' && data.expectJson) {
      const example = data.outputExample
      if (example && typeof example === 'object' && !Array.isArray(example)) {
        const fields = extractFieldPaths(example)
        if (fields.length > 0) {
          // eslint-disable-next-line security/detect-object-injection
          nodeOutputKeys[n.id] = fields.slice().sort()
        }
      }
    }
  })

  return {
    availableNodeIds,
    triggerKeys: Array.from(triggerKeys).sort(),
    nodeOutputKeys,
    loopVariableNames,
    nodeMeta,
  }
}

const AutocompleteScope: FC<{
  nodes: Node[]
  edges: Edge[]
  selectedNodeId: string | null
  variableNames: string[]
  children: ReactNode
}> = ({ nodes, edges, selectedNodeId, variableNames, children }) => {
  const inputs = useMemo(
    () => deriveAutocompleteInputs(nodes, edges, selectedNodeId),
    [nodes, edges, selectedNodeId],
  )
  return (
    <ExpressionAutocompleteProvider
      availableNodeIds={inputs.availableNodeIds}
      variableNames={variableNames}
      triggerKeys={inputs.triggerKeys}
      nodeOutputKeys={inputs.nodeOutputKeys}
      loopVariableNames={inputs.loopVariableNames}
      nodeMeta={inputs.nodeMeta}
    >
      {children}
    </ExpressionAutocompleteProvider>
  )
}

const VARIABLE_REF_RE = /\{\{\s*variables\.([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g

const deriveVariableNames = (nodes: Node[]): string[] => {
  // Suggest variables already referenced in this session, even before saving.
  const names = new Set<string>()
  const visit = (value: unknown) => {
    if (typeof value === 'string') {
      let m: RegExpExecArray | null
      VARIABLE_REF_RE.lastIndex = 0
      // eslint-disable-next-line no-cond-assign
      while ((m = VARIABLE_REF_RE.exec(value)) !== null) {
        names.add(m[1])
      }
    } else if (Array.isArray(value)) {
      value.forEach(visit)
    } else if (value && typeof value === 'object') {
      Object.values(value as Record<string, unknown>).forEach(visit)
    }
  }
  nodes.forEach((n) => visit(n.data))
  return Array.from(names).sort()
}

interface WorkflowEditorPageProps {
  onSave?: (name: string, nodes: Node[], edges: Edge[]) => void
  initialNodes?: Node[]
  initialEdges?: Edge[]
  initialName?: string
  workflowId?: string
  testConfig?: Record<string, unknown>
}

// Dirty-detection fingerprint: only what persistSave persists, no transient xyflow fields.
const serializeGraph = (nodes: Node[], edges: Edge[]): string =>
  JSON.stringify({
    nodes: nodes.map((n) => ({
      id: n.id,
      x: Math.round(n.position.x),
      y: Math.round(n.position.y),
      data: n.data,
    })),
    edges: edges.map((e) => ({ id: e.id, source: e.source, target: e.target })),
  })

const WorkflowEditorInner: FC<WorkflowEditorPageProps> = ({
  onSave,
  initialNodes = [],
  initialEdges = [],
  initialName,
  workflowId,
  testConfig,
}) => {
  const {
    nodes,
    edges,
    selectedNode,
    selectedNodeId,
    workflowName,
    setWorkflowName,
    onNodesChange,
    onEdgesChange,
    onConnect,
    addNode,
    removeNode,
    updateNodeData,
    renameNode,
    selectNode,
    undo,
    setNodes,
    setEdges,
  } = useWorkflowState(initialNodes, initialEdges)

  const translate = useTranslate()
  const [copilotOpen, setCopilotOpen] = useState(false)
  const [validationResult, setValidationResult] = useState<WorkflowValidationResult | null>(null)
  const autoLayout = useAutoLayout(nodes, edges, setNodes)
  const { functionNames } = useFunctionsRegistry()
  const variableNames = useMemo(() => deriveVariableNames(nodes), [nodes])

  const [savedSnapshot, setSavedSnapshot] = useState(() =>
    serializeGraph(initialNodes, initialEdges),
  )
  const isDirty = useMemo(
    () => serializeGraph(nodes, edges) !== savedSnapshot,
    [nodes, edges, savedSnapshot],
  )
  const { confirmOpen, confirmLeave, cancelLeave } = useUnsavedChangesGuard(isDirty)

  const handleCopilotApply = useCallback(
    (nextNodes: Node[], nextEdges: Edge[]) => {
      setNodes(nextNodes)
      setEdges(nextEdges)
    },
    [setNodes, setEdges],
  )

  const handleDrop = useCallback(
    (position: { x: number; y: number }, dragData: SidebarDragData) => {
      addNode(position, dragData)
    },
    [addNode],
  )

  const handleNodeClick = useCallback(
    (nodeId: string) => {
      selectNode(nodeId)
    },
    [selectNode],
  )

  const handlePaneClick = useCallback(() => {
    selectNode(null)
  }, [selectNode])

  const handleUpdateNodeData = useCallback(
    (data: Partial<WorkflowNodeData>) => {
      if (selectedNodeId) {
        updateNodeData(selectedNodeId, data)
      }
    },
    [selectedNodeId, updateNodeData],
  )

  const handleDeleteNode = useCallback(() => {
    if (selectedNodeId) {
      removeNode(selectedNodeId)
    }
  }, [selectedNodeId, removeNode])

  const persistSave = useCallback(() => {
    if (!onSave) return
    // Strip self-loop edges (possible via Co-pilot/Generate): the engine
    // rejects them as a cycle and the run hangs in RUNNING.
    const cleanEdges = edges.filter((e) => e.source !== e.target)
    if (cleanEdges.length !== edges.length) {
      setEdges(cleanEdges)
    }
    onSave(workflowName, nodes, cleanEdges)
    setSavedSnapshot(serializeGraph(nodes, cleanEdges))
  }, [onSave, workflowName, nodes, edges, setEdges])

  const handleSave = useCallback(() => {
    if (!onSave) return
    // Validate against the same self-loop-stripped edges persistSave will use.
    const cleanEdges = edges.filter((e) => e.source !== e.target)
    const result = validateWorkflow({ nodes, edges: cleanEdges, variableNames, functionNames })
    if (result.errors.length === 0) {
      persistSave()
      return
    }
    setValidationResult(result)
  }, [onSave, nodes, edges, variableNames, functionNames, persistSave])

  const handleJumpToValidationNode = useCallback(
    (nodeId: string) => {
      setValidationResult(null)
      selectNode(nodeId)
    },
    [selectNode],
  )

  const handleSaveAnyway = useCallback(() => {
    setValidationResult(null)
    persistSave()
  }, [persistSave])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // Fill the viewport below RaLayout's 72px top margin; cancel its
        // bottom margin so the page never scrolls.
        height: 'calc(100vh - 72px)',
        mx: { sm: -6, xs: 0 },
        mb: { sm: -6, xs: 0 },
      }}
    >
      <WorkflowToolbar
        workflowName={initialName ?? workflowName}
        onNameChange={setWorkflowName}
        onAutoLayout={autoLayout}
        onUndo={undo}
        onSave={onSave ? handleSave : undefined}
        onToggleCopilot={
          workflowId
            ? () => {
              setCopilotOpen((v) => {
                const next = !v
                if (next) {
                  // Collapse the node-config panel so two right-rail panels don't compete.
                  selectNode(null)
                }
                return next
              })
            }
            : undefined
        }
        copilotOpen={copilotOpen}
      />
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <WorkflowSidebar />
        <WorkflowCanvas
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={handleNodeClick}
          onPaneClick={handlePaneClick}
          onDrop={handleDrop}
          selectedNodeId={selectedNodeId}
        />
        <AutocompleteScope
          nodes={nodes}
          edges={edges}
          selectedNodeId={selectedNodeId}
          variableNames={variableNames}
        >
          <NodeConfigPanel
            node={selectedNode}
            onUpdate={handleUpdateNodeData}
            onRenameNode={renameNode}
            onDelete={handleDeleteNode}
            onClose={() => selectNode(null)}
            workflowId={workflowId}
            testConfig={testConfig}
          />
        </AutocompleteScope>
        {copilotOpen && workflowId && (
          <CopilotPanel
            workflowId={workflowId}
            nodes={nodes}
            edges={edges}
            onApply={handleCopilotApply}
            onClose={() => setCopilotOpen(false)}
          />
        )}
      </Box>
      <WorkflowValidationDialog
        open={validationResult !== null}
        result={validationResult}
        nodeLabel={(id) => {
          const node = nodes.find((n) => n.id === id)
          const data = node?.data as { label?: string } | undefined
          return data?.label ?? id
        }}
        onCancel={() => setValidationResult(null)}
        onJumpToNode={handleJumpToValidationNode}
        onSaveAnyway={handleSaveAnyway}
      />
      <Dialog open={confirmOpen} onClose={cancelLeave} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {translate('workflow.editor.unsavedTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.875rem' }}>
            {translate('workflow.editor.unsavedBody')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={confirmLeave} color="error" sx={{ textTransform: 'none' }}>
            {translate('workflow.editor.unsavedLeave')}
          </Button>
          <Button onClick={cancelLeave} variant="contained" sx={{ textTransform: 'none' }}>
            {translate('workflow.editor.unsavedStay')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

const WorkflowEditorPage: FC<WorkflowEditorPageProps> = (props) => (
  <ReactFlowProvider>
    <WorkflowEditorInner {...props} />
  </ReactFlowProvider>
)

export default WorkflowEditorPage
